import { useSearchParams } from 'react-router-dom';
import {
  calendarDateISO,
  parseCalendarDate,
  salesDateRange,
  salesBusinessDate,
} from '@klothiq/domain';
import { useAuth } from '@/contexts/AuthContext';
export interface DashboardRange {
  from: string;
  to: string;
}
export function useDashboardRange() {
  const { organization } = useAuth();
  const [params, setParams] = useSearchParams();
  const today = salesBusinessDate(organization?.timezone);
  const defaultRange = salesDateRange('month', organization?.timezone);
  const from = params.get('from'),
    to = params.get('to');
  const valid =
    !!from &&
    !!to &&
    !!parseCalendarDate(from) &&
    !!parseCalendarDate(to) &&
    from <= to &&
    to <= today;
  const range: DashboardRange = valid ? { from: from!, to: to! } : defaultRange;
  function apply(next: DashboardRange) {
    if (
      !parseCalendarDate(next.from) ||
      !parseCalendarDate(next.to) ||
      next.from > next.to ||
      next.to > today
    )
      throw new Error('Choose valid dates ending on or before today.');
    setParams((previous) => {
      const p = new URLSearchParams(previous);
      p.set('from', next.from);
      p.set('to', next.to);
      return p;
    });
  }
  return {
    range,
    today,
    label: `${range.from} – ${range.to}`,
    invalid: !!(from || to) && !valid,
    apply,
    reset: () =>
      setParams((p) => {
        const n = new URLSearchParams(p);
        n.delete('from');
        n.delete('to');
        return n;
      }),
    month: (value: string) => {
      if (!/^\d{4}-\d{2}$/.test(value)) return;
      const [y, m] = value.split('-').map(Number);
      apply({ from: `${value}-01`, to: [calendarDateISO(new Date(y!, m!, 0)), today].sort()[0]! });
    },
  };
}
